/**
 * signupAttribution — captures incoming URL parameters on first landing
 * (?ref=, ?staff=, ?campaign=, utm_*) and the document.referrer, persists
 * them in localStorage, and exposes helpers to read & later attach the
 * blob to a freshly-created customer's `customer_prefs.data.signup_attribution`.
 *
 * The blob is captured ONCE per visitor (first non-empty hit wins) so a
 * customer who lands via a referral link and then bounces around the site
 * still gets credited correctly when they finally sign up.
 */
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "khays_signup_attribution";

export interface SignupAttribution {
  source: "main" | "referral" | "staff" | "campaign";
  referralUsername?: string;   // ?ref=<username> — customer or staff referral
  staffUsername?: string;      // ?staff=<username> — explicit staff link
  campaignId?: string;         // ?campaign=<id>
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
  landingUrl: string;          // full URL on first landing
  landingPath: string;         // pathname only
  referrer: string | null;     // document.referrer at first landing
  capturedAt: number;          // ms epoch
}

function readStored(): SignupAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SignupAttribution;
  } catch {
    return null;
  }
}

function writeStored(blob: SignupAttribution): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(blob)); } catch {}
}

/**
 * Run on app boot. Parses URL params and only writes if no prior capture
 * exists OR the new hit carries explicit attribution params (ref/staff/
 * campaign/utm_*) — those should overwrite a previous direct visit.
 */
export function captureSignupAttributionFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref")?.trim() || "";
    const staff = params.get("staff")?.trim() || "";
    const campaign = params.get("campaign")?.trim() || "";
    const utmSource = params.get("utm_source")?.trim() || "";
    const utmMedium = params.get("utm_medium")?.trim() || "";
    const utmCampaign = params.get("utm_campaign")?.trim() || "";
    const utmTerm = params.get("utm_term")?.trim() || "";
    const utmContent = params.get("utm_content")?.trim() || "";

    const hasExplicit = !!(ref || staff || campaign || utmSource || utmMedium || utmCampaign);
    const existing = readStored();
    if (existing && !hasExplicit) return; // keep first-touch

    let source: SignupAttribution["source"] = "main";
    if (staff) source = "staff";
    else if (ref) source = "referral";
    else if (campaign || utmCampaign) source = "campaign";

    const blob: SignupAttribution = {
      source,
      referralUsername: ref || undefined,
      staffUsername: staff || undefined,
      campaignId: campaign || utmCampaign || undefined,
      utm: (utmSource || utmMedium || utmCampaign || utmTerm || utmContent)
        ? { source: utmSource || undefined, medium: utmMedium || undefined,
            campaign: utmCampaign || undefined, term: utmTerm || undefined, content: utmContent || undefined }
        : undefined,
      landingUrl: window.location.href,
      landingPath: window.location.pathname + window.location.search,
      referrer: document.referrer || null,
      capturedAt: Date.now(),
    };
    writeStored(blob);
  } catch {
    /* noop */
  }
}

export function getSignupAttribution(): SignupAttribution | null {
  return readStored();
}

export function clearSignupAttribution(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

/**
 * Persist the captured attribution to `customer_prefs.data.signup_attribution`
 * for a freshly authenticated customer. Idempotent: keeps the existing value
 * if one is already stored server-side.
 */
export async function persistSignupAttribution(userId: string): Promise<void> {
  if (!userId) return;
  const blob = readStored();
  try {
    const { data: existing } = await supabase
      .from("customer_prefs")
      .select("data")
      .eq("customer_user_id", userId)
      .maybeSingle();
    const current = ((existing?.data as any) || {}) as Record<string, any>;
    if (current.signup_attribution || current.registration_notified) {
      // Already processed once — preserve first-write-wins for both
      // attribution persistence and customer_registration emission.
      return;
    }
    if (!blob) {
      // No attribution captured (direct signup). Still emit a generic
      // customer_registration notification, then mark as notified so we
      // don't double-fire on subsequent sign-ins.
      try {
        const { getCurrentTenantId } = await import("@/lib/tenantContext");
        await supabase.from("customer_prefs").upsert(
          {
            customer_user_id: userId,
            tenant_id: getCurrentTenantId(),
            data: { ...current, registration_notified: true } as any,
          },
          { onConflict: "customer_user_id" },
        );
      } catch { /* noop */ }
      void notifyCustomerRegistration(userId, null).catch(() => { /* swallow */ });
      return;
    }
    const next = { ...current, signup_attribution: blob };
    const { getCurrentTenantId } = await import("@/lib/tenantContext");
    await supabase.from("customer_prefs").upsert(
      { customer_user_id: userId, tenant_id: getCurrentTenantId(), data: next as any },
      { onConflict: "customer_user_id" }
    );

    // Fire a real-time admin notification when this signup is attributed
    // to a staff/referral link so the responsible staff (and admins) see it
    // immediately in the Notification Center.
    void notifyStaffOfAttributedSignup(userId, blob).catch(() => { /* swallow */ });
    // Phase 5: always emit a generic customer_registration notification so
    // Admins/Managers/Store Managers are alerted regardless of attribution.
    void notifyCustomerRegistration(userId, blob).catch(() => { /* swallow */ });
  } catch (e) {
    console.warn("persistSignupAttribution failed", e);
  }
}

async function notifyCustomerRegistration(
  userId: string,
  blob: SignupAttribution | null,
): Promise<void> {
  try {
    const { data: cust } = await supabase
      .from("customers")
      .select("full_name,email")
      .eq("user_id", userId)
      .maybeSingle();
    const name = (cust?.full_name || cust?.email || "A new customer").trim();
    const email = (cust?.email || "").trim();
    let tenantId: string | null = null;
    try {
      const { getCurrentTenantId } = await import("@/lib/tenantContext");
      tenantId = getCurrentTenantId();
    } catch { /* noop */ }
    const sourceLabel = summarizeAttribution(blob);
    const { notify, buildIdempotencyKey } = await import("@/lib/notificationService");
    await notify({
      type: "customer_registration",
      title: `New customer: ${name}`,
      body: `${name}${email ? ` (${email})` : ""} registered via ${sourceLabel}.`,
      tenantId,
      channels: ["in_app", "email"],
      idempotencyKey: buildIdempotencyKey(["customer_registration", userId]),
      emailTemplate: "admin-notification",
      emailData: {
        customerName: name,
        customerEmail: email,
        source: sourceLabel,
        landingPath: blob?.landingPath,
      },
      metadata: {
        notification_type: "customer_registration",
        customer_user_id: userId,
        source: blob?.source || "main",
      },
    });
  } catch (e) {
    console.warn("notifyCustomerRegistration failed", e);
  }
}

async function notifyStaffOfAttributedSignup(
  userId: string,
  blob: SignupAttribution,
): Promise<void> {
  if (blob.source !== "staff" && blob.source !== "referral") return;
  const username = (blob.staffUsername || blob.referralUsername || "").trim();
  if (!username) return;

  // Fetch the customer's display name for a friendlier notification body.
  let customerName = "A new customer";
  try {
    const { data: cust } = await supabase
      .from("customers")
      .select("full_name,email")
      .eq("user_id", userId)
      .maybeSingle();
    customerName = (cust?.full_name || cust?.email || "A new customer").trim();
  } catch { /* noop */ }

  // Resolve the target staff user by username so the notification routes
  // directly to them. Falls back to broadcast (target_user_id = null) if
  // the username can't be matched.
  let targetUserId: string | null = null;
  try {
    const { data: prof } = await supabase
      .from("profiles")
      .select("user_id")
      .eq("username", username)
      .maybeSingle();
    targetUserId = (prof?.user_id as string | undefined) ?? null;
  } catch { /* noop */ }

  const sourceLabel = blob.source === "staff" ? "staff link" : "referral link";
  let tenantId: string | null = null;
  try {
    const { getCurrentTenantId } = await import("@/lib/tenantContext");
    tenantId = getCurrentTenantId();
  } catch { /* noop */ }
  await supabase.from("admin_notifications").insert({
    type: "referral_signup",
    title: `New signup via ${username}'s ${sourceLabel}`,
    body: `${customerName} just signed up using ${username}'s ${sourceLabel}.`,
    target_user_id: targetUserId,
    tenant_id: tenantId,
    metadata: {
      notification_type: "referral_signup",
      attributed_to: username,
      source: blob.source,
      campaign_id: blob.campaignId || null,
      landing_path: blob.landingPath,
      customer_user_id: userId,
      tenant_id: tenantId,
    } as any,
  });
}

export function summarizeAttribution(blob?: SignupAttribution | null): string {
  if (!blob) return "Direct";
  switch (blob.source) {
    case "staff": return `Staff link (${blob.staffUsername || "unknown"})`;
    case "referral": return `Referral (${blob.referralUsername || "unknown"})`;
    case "campaign": return `Campaign (${blob.campaignId || blob.utm?.campaign || "unknown"})`;
    default: return "Main / Direct";
  }
}
